<template>
    <v-container fluid class="pa-4">
        <v-row class="mb-2">
            <v-col cols="12" class="d-flex align-center justify-space-between">
                <div>
                    <h1 class="text-h5 font-weight-bold">Expiring Documents</h1>
                    <p class="text-body-2 text-grey mt-1">Driver documents already expired or expiring within the selected window</p>
                </div>
                <v-btn icon="mdi-refresh" variant="text" :loading="loading" @click="load"/>
            </v-col>
        </v-row>

        <v-row class="mb-3" align="center">
            <v-col v-if="user.type === 'admin'" cols="12" md="3" sm="6">
                <company-select v-model="company" :items="companiesList" density="compact" label="Company"
                                @update:model-value="load"/>
            </v-col>
            <v-col cols="12" md="2" sm="4">
                <v-select v-model="days" :items="dayOptions" item-title="label" item-value="value"
                          density="compact" label="Expiry window" @update:model-value="load"/>
            </v-col>
        </v-row>

        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3"/>

        <v-card elevation="2" class="pa-4">
            <v-table density="compact" hover>
                <thead>
                <tr>
                    <th>Driver</th>
                    <th>Document</th>
                    <th>Type</th>
                    <th class="text-center">Expires On</th>
                    <th class="text-center">Days Left</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="doc in docs" :key="doc.id">
                    <td>{{ doc.driver_name || '-' }}</td>
                    <td>{{ doc.name }}</td>
                    <td>{{ doc.doc_type || '-' }}</td>
                    <td class="text-center">{{ doc.expires_on }}</td>
                    <td class="text-center">
                        <v-chip v-if="doc.expired" size="x-small" variant="flat" color="error">
                            Expired {{ -doc.days_to_expiry }}d ago
                        </v-chip>
                        <v-chip v-else size="x-small" variant="flat" :color="daysColor(doc.days_to_expiry)">
                            {{ doc.days_to_expiry === 0 ? 'Today' : `${doc.days_to_expiry}d` }}
                        </v-chip>
                    </td>
                </tr>
                </tbody>
            </v-table>
            <div v-if="!docs.length && !loading" class="text-center text-grey py-6">
                No documents expired or expiring within {{ days }} days.
            </div>
        </v-card>

        <v-snackbar v-model="error" color="error" timeout="3000">{{ errorMsg }}</v-snackbar>
    </v-container>
</template>

<script setup>
import {onMounted, ref, watch} from 'vue'
import {useRoute} from 'vue-router'
import {send} from '@/composables/apiClient'
import {useAuthStore} from '@/stores/authStore'
import CompanySelect from "@/components/globals/company-select.vue";

const {user} = useAuthStore()
const route = useRoute()

const docs = ref([])
const loading = ref(false)
const error = ref(false)
const errorMsg = ref('')

const company = ref(user.type !== 'admin' ? user.company?.id : null)
const companiesList = ref([])
const dayOptions = [
    {label: '7 days', value: 7},
    {label: '14 days', value: 14},
    {label: '30 days', value: 30},
    {label: '60 days', value: 60},
    {label: '90 days', value: 90},
]
// The menu count opens this page with ?days=30.
const queryDays = Number(route.query.days)
const days = ref(dayOptions.some((o) => o.value === queryDays) ? queryDays : 60)
watch(() => route.query.days, (value) => {
    if (route.name !== 'fleet/reports/expiring-documents') return
    const n = Number(value)
    days.value = dayOptions.some((o) => o.value === n) ? n : 60
    load()
})

const daysColor = (d) => {
    if (d <= 7) return 'error'
    if (d <= 30) return 'warning'
    return 'success'
}

const load = async () => {
    loading.value = true
    try {
        const params = {days: days.value}
        if (user.type === 'admin') params.company = company.value
        const res = await send('get', 'companies/drivers/documents/expiring', params)
        docs.value = res?.data?.data ?? []
    } catch (e) {
        errorMsg.value = 'Could not load expiring documents.'
        error.value = true
    } finally {
        loading.value = false
    }
}

const fetchCompanies = async () => {
    try {
        const res = await send('get', 'companies/list')
        companiesList.value = res?.data?.data ?? []
    } catch {}
}

onMounted(() => {
    if (user.type === 'admin') fetchCompanies()
    load()
})
</script>
